import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import api from '../../../lib/api';

const AcquisitionFunnel = () => {
  const [funnelData, setFunnelData] = useState([]);
  const [conversionRate, setConversionRate] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hoveredStage, setHoveredStage] = useState(null);

  useEffect(() => {
    const fetchFunnelData = async () => {
      try {
        setLoading(true);

        // Fetch member counts by status
        const { data: members } = await api.get('/members', { params: { limit: 2000 } });

        if (!members || members?.length === 0) {
          setLoading(false);
          return;
        }

        const total = members?.length;
        const active = members?.filter(m => m?.member_status === 'active')?.length;
        const trial = members?.filter(m => m?.member_status === 'trial')?.length;
        const inactive = members?.filter(m => m?.member_status === 'inactive')?.length;

        // Build funnel stages from real data
        const stages = [
          { stage: "Total Registered", count: total, percentage: 100, color: "#0F766E", dropOff: null },
          { stage: "Active Members", count: active + trial, percentage: Math.round(((active + trial) / total) * 100), color: "#0D9488", dropOff: null },
          { stage: "Fully Active", count: active, percentage: Math.round((active / total) * 100), color: "#14B8A6", dropOff: null },
        ];

        // Add drop-off percentages
        for (let i = 1; i < stages?.length; i++) {
          const prev = stages?.[i - 1]?.percentage;
          const curr = stages?.[i]?.percentage;
          stages[i].dropOff = prev > 0 ? `${(prev - curr)?.toFixed(0)}%` : null;
        }

        setFunnelData(stages);
        setConversionRate(stages?.[stages?.length - 1]?.percentage || 0);
      } catch (err) {
        console.error('Error fetching funnel data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFunnelData();
  }, []);

  if (loading) {
    return (
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-6 shadow-sm h-full flex items-center justify-center">
        <span className="text-sm text-[var(--color-muted-foreground)] font-caption">Loading funnel data...</span>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-4 lg:p-6 shadow-sm h-full">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-base lg:text-lg font-semibold font-heading text-[var(--color-foreground)]">
            Member Conversion Funnel
          </h3>
          <p className="text-xs text-[var(--color-muted-foreground)] font-caption mt-0.5">
            Stage-by-stage member status breakdown
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-[var(--color-primary)] font-caption font-medium">
          <Icon name="TrendingUp" size={14} color="var(--color-primary)" />
          <span>{conversionRate}% active rate</span>
        </div>
      </div>
      {funnelData?.length === 0 ? (
        <div className="text-center py-8 text-sm text-[var(--color-muted-foreground)] font-caption">
          No member data available yet.
        </div>
      ) : (
        <div className="space-y-3">
          {funnelData?.map((item, index) => (
            <div
              key={item?.stage}
              className="relative cursor-pointer group"
              onMouseEnter={() => setHoveredStage(index)}
              onMouseLeave={() => setHoveredStage(null)}
            >
              {item?.dropOff && (
                <div className="flex items-center gap-2 mb-1.5 ml-2">
                  <Icon name="ArrowDown" size={12} color="var(--color-error)" />
                  <span className="text-xs text-[var(--color-error)] font-caption">
                    {item?.dropOff} drop-off
                  </span>
                </div>
              )}
              <div
                className="relative rounded-lg overflow-hidden transition-all duration-200"
                style={{
                  backgroundColor: hoveredStage === index ? `${item?.color}22` : `${item?.color}12`,
                  border: `1px solid ${hoveredStage === index ? item?.color : 'transparent'}`,
                }}
              >
                <div
                  className="h-10 lg:h-12 rounded-lg transition-all duration-500 flex items-center px-3 lg:px-4"
                  style={{
                    width: `${Math.max(item?.percentage, 15)}%`,
                    minWidth: '120px',
                    backgroundColor: item?.color,
                  }}
                >
                  <span className="text-white text-xs font-medium font-caption whitespace-nowrap">
                    {item?.stage}
                  </span>
                </div>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  <span className="text-sm font-bold font-heading text-[var(--color-foreground)]">
                    {item?.count?.toLocaleString()}
                  </span>
                  <span className="text-xs text-[var(--color-muted-foreground)] font-caption">
                    ({item?.percentage}%)
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-5 pt-4 border-t border-[var(--color-border)] grid grid-cols-2 gap-3">
        <div className="text-center">
          <p className="text-lg font-bold font-heading text-[var(--color-primary)]">{conversionRate}%</p>
          <p className="text-xs text-[var(--color-muted-foreground)] font-caption">Active Rate</p>
        </div>
        <div className="text-center border-l border-[var(--color-border)]">
          <p className="text-lg font-bold font-heading text-[var(--color-foreground)]">{funnelData?.[0]?.count || 0}</p>
          <p className="text-xs text-[var(--color-muted-foreground)] font-caption">Total Members</p>
        </div>
      </div>
    </div>
  );
};

export default AcquisitionFunnel;